import { NavLink } from "react-router-dom";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ALL_RECORDS, NAV_RECORDS } from "@/lib/nav-records";
import { isRouteAuthorized } from "@/lib/routes";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

/**
 * The shared record bar — Accounts, Clients, Deals, in a horizontal strip
 * directly under the topbar. It renders only in the modules that set
 * `ModuleDefinition.recordBar` (Energy Advisor and Operations). Market
 * Intelligence, Reports and Admin don't show it (AB#8790).
 *
 * **Why horizontal, and why here.** These records aren't part of the module
 * you're in; they're the layer every module sits on. When they lived as a
 * "Records" section *inside* the sidebar, they rendered as a peer of
 * "Workspace" and "Operations" under the Sales pill — visually implying they
 * belonged to Sales, which is the opposite of the truth. No label fixes that,
 * because every label inherits the same styling and position.
 *
 * Salesforce Lightning solves it exactly this way: one horizontal bar of
 * object tabs below the global header, outside whichever module's nav is
 * showing. (M365 solves the same problem on the other axis — a persistent
 * left icon rail — but that fights a product that already uses the left edge
 * for app nav.) Putting the records in their own region, in different real
 * estate from the sidebar, is what communicates "different scope."
 *
 * Gated per entry by the same predicate routes use, so a record the caller
 * can't reach isn't advertised. The whole bar hides when nothing survives —
 * an empty strip is just wasted vertical space.
 */
export function RecordBar() {
  const session = useAuthSession();
  // Below `sm` the bar would wrap to two rows and eat the viewport on a
  // phone. The records stay reachable there through the sidebar drawer's own
  // nav, so dropping the strip loses nothing but the shortcut.
  const wideEnough = useMediaQuery("(min-width: 640px)");

  const items = ALL_RECORDS.map((key) => NAV_RECORDS[key]).filter((m) =>
    isRouteAuthorized(
      { ...m.navRoute, status: "live" },
      session.is_super_admin,
      session.features,
      session.permissions,
      session.is_supplier,
    ),
  );

  if (!wideEnough || items.length === 0) return null;

  // Frosted-glass pane, matching the sticky structure strip in
  // `energy-advisor/rfq/components/OfferingTabs.tsx` (`bg-card/80 shadow-card
  // backdrop-blur-md`) — same job, so the same treatment.
  //
  // Glass rather than a flat fill because it reads as a layer floating ABOVE
  // the module rather than another opaque band bolted onto the topbar — which is
  // exactly the "these records aren't part of this module" message. A solid
  // `bg-card` worked but made the topbar + strip read as one tall block.
  //
  // `--card` at 80% is one step lighter than the topbar's
  // `--background-deep` in every palette (checked across all 8 mode ×
  // palette combinations), so the strip lifts consistently. Not
  // `bg-background`: that token is deliberately `transparent` in the light
  // theme — a window onto the gradient `html` paints — so it would give an
  // uncontrolled tint rather than a chosen one.
  //
  // Tried and rejected: the launcher's ambient radial washes
  // (`--primary-dim` + `--orange-dim`) shining through a more transparent
  // pane. Far too loud for persistent chrome — the gold pooled at the right
  // edge and competed with the page content. Those glows work on the
  // launcher because it's a full-page landing surface you pass through, not
  // a strip you look at all day.
  //
  // Deliberately NOT sticky. It doesn't need to be — it's a flex child above
  // the scrolling `<main>`, so it stays put anyway. And `sticky top-chrome` here
  // would collide with `OfferingTabs`'s structure strip, which pins at that
  // exact offset on the RFQ walkthrough: this bar renders above the outlet,
  // so that strip would slide underneath it.
  return (
    <nav
      aria-label="Records"
      className="flex shrink-0 items-center gap-1 border-b border-border bg-card/80 px-4 shadow-card backdrop-blur-md md:px-6"
      style={{
        transition:
          "background var(--duration-theme), border-color var(--duration-theme)",
      }}
    >
      {/* Always the bare list path, in every module. Per-client access is
          the sidebar's job (see `RecordScopeSection`) -- this bar is the
          global record layer, and keeping it global is what makes it mean
          the same thing everywhere. */}
      {items.map((m) => {
        const Icon = m.icon;
        return (
          <NavLink
            key={m.key}
            to={m.navRoute.path}
            className={({ isActive }) => rowClass(isActive)}
          >
            <Icon size={14} strokeWidth={1.75} />
            {m.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * Row classes for a record tab. Lives here rather than in `RecordBarLinks`
 * so the bar keeps ownership of its own visual contract.
 *
 * The active tab carries a bottom border that meets the bar's own — the
 * standard tab affordance, not a pill, so the strip stays visually quieter
 * than the sidebar's nav rows.
 */
function rowClass(isActive: boolean): string {
  return cn(
    "flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-sm no-underline transition-colors",
    isActive
      ? "border-primary font-semibold text-primary"
      : "border-transparent text-muted-foreground hover:text-foreground",
  );
}
